import { useEffect, useState } from "react";
import {
  Autocomplete,
  Grid,
  TextField,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { BASE_URL } from "../../../constants";
import MicroBioInvestigation from "./MicroBiologyInvestigation";
import dayjs from "dayjs";
import { format } from "date-fns";
import UpdateIcon from "@mui/icons-material/Update";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { useLocation } from "react-router-dom";

const MicroBiology = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [investigations, setInvestigations] = useState([]);
  const [result, setResult] = useState(""); // Positive/Negative
  const [quillText, setQuillText] = useState("");
  const [selectedOrganism, setSelectedOrganism] = useState("");
  const [sensitive, setSensitive] = useState([]);
  const [intermediate, setIntermediate] = useState([]);
  const [resistant, setResistant] = useState([]);
  const [sampleCollectedDate, setSampleCollectedDate] = useState(
    format(new Date(), "MMMM d, yyyy"),
  );
  const [sampleCollectedTime, setSampleCollectedTime] = useState(
    format(new Date(), "hh:mm a"),
  );
  const [openModal, setOpenModal] = useState(false);

  const handleDateChange = (newDate) => {
    if (newDate) {
      const formattedDate = dayjs(newDate).format("MMMM D, YYYY");
      setSampleCollectedDate(formattedDate);
    }
    //setOpenModal(false);
  };

  const handleTimeChange = (newDate) => {
    if (newDate) {
      // Format the selected time as hh:mm AM/PM
      const formattedTime = format(new Date(newDate), "hh:mm a");
      setSampleCollectedTime(formattedTime);
    }
  };
  const fetchAllInvoices = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}api/user/microbiology/get-all-microbiology-invoices?invoiceFor=microbiology`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();
      setInvoices(data.invoices);
    } catch (error) {
      console.error("Error:", error);
      alert("Something went wrong! Please try again.");
    }
  };

  const fetchInvestigations = async (ids) => {
    try {
      setLoading(true);
      const allIds = ids.map((id) => {
        return id._id;
      });
      const response = await fetch(
        `${BASE_URL}api/user/microbiology/get-microbiology-investigation-for-testIDs`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ testIDs: allIds }),
        },
      );
      const data = await response.json();
      if (location?.state?.micro) {
        const sMap = new Map();
        location?.state?.micro?.report.forEach((item) => {
          sMap.set(item.testId._id, item);
        });

        // Merge data from s into i
        const updatedI = data.investigations.map((item) => {
          const matching = sMap.get(item.testId._id);
          if (matching) {
            return {
              ...item,
              investigation: matching.investigation,
              doctorSignatory: matching.doctorSignatory,
              techSignatory: matching.techSignatory,
            };
          }
          return item;
        });
        setInvestigations(updatedI);
      } else {
        setInvestigations(data.investigations || []);
      }
    } catch (error) {
      alert("Something went wrong! Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location?.state?.micro) {
      const ids = location?.state?.micro?.report?.map((test) => test.testId);
      ids && fetchInvestigations(ids);
      setSelectedInvoice(location?.state?.micro);
    } else {
      fetchAllInvoices();
      const ids = selectedInvoice?.tests?.map((test) => test.testId);
      ids && fetchInvestigations(ids);
    }
  }, [selectedInvoice]);

  const compoundList = [
    ...new Set(investigations.flatMap((inv) => inv.compound || [])),
  ];
  const organismList = [
    ...new Set(investigations.flatMap((inv) => inv.organism || [])),
  ];

  const handleCompoundSelect = (value, type) => {
    const updater = {
      sensitive: setSensitive,
      intermediate: setIntermediate,
      resistant: setResistant,
    }[type];

    updater((prev) => [...new Set([...prev, value])]);
  };

  const calculateAge = (dob) => {
    const birthDate = dayjs(dob, "MMMM DD, YYYY");
    const today = dayjs();
    const diffInDays = today.diff(birthDate, "days");
    if (diffInDays < 30) {
      return `${diffInDays} days`;
    }
    let months = today.diff(birthDate, "months");
    if (today.date() < birthDate.date()) {
      months--;
    }
    if (months < 12) {
      return `${months} months`;
    }
    let years = Math.floor(months / 12);
    return `${years} years`;
  };

  const formatDateAndTime = (timestamp) => {
    const date = new Date(timestamp);
    const formattedDate = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

    return `${formattedDate} | ${formattedTime}`;
  };

  return (
    <>
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change sample collected date</DialogTitle>
        <DialogContent
          sx={{
            paddingTop: "10px !important",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Appointment Date"
              value={
                sampleCollectedDate
                  ? dayjs(sampleCollectedDate, "MMMM D, YYYY")
                  : null
              }
              onChange={handleDateChange}
              format="DD.MM.YYYY"
              maxDate={dayjs()}
              renderInput={(params) => (
                <TextField {...params} size="small" fullWidth />
              )}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              label="Appointment Time"
              onChange={handleTimeChange}
              value={
                sampleCollectedTime
                  ? dayjs(sampleCollectedTime, "hh:mm A")
                  : null
              }
              renderInput={(params) => (
                <TextField {...params} size="small" fullWidth />
              )}
            />
          </LocalizationProvider>
        </DialogContent>
      </Dialog>
      {invoices?.length > 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={invoices}
              getOptionLabel={(option) =>
                `${option?.invoiceId} (${option?.patientId?.name} ~ ${option?.patientId?.patientID})`
              }
              onChange={(event, newValue) => {
                setSelectedInvoice(newValue);
                setResult("");
                setQuillText("");
                setSelectedOrganism("");
                setSensitive([]);
                setIntermediate([]);
                setResistant([]);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Invoice"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableClearable
            />
          </Grid>
        </Grid>
      )}
      {selectedInvoice && investigations && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mt: 2,
            mb: 2,
            backgroundColor: "#f5f5f5",
            borderLeft: "5px solid #1976d2",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Name:</strong> {selectedInvoice?.patientId?.name}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Age:</strong>{" "}
                {calculateAge(selectedInvoice?.patientId?.dob)}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Gender:</strong> {selectedInvoice?.patientId?.gender}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Ref By:</strong> {selectedInvoice?.doctorId?.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1">
                <strong>Registration ID: </strong>{" "}
                {selectedInvoice?.invoiceId || selectedInvoice?.invoice}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Registration Date: </strong>{" "}
                {formatDateAndTime(selectedInvoice?.createdAt)}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <strong style={{ marginRight: "5px" }}>
                  Sample Collected:
                </strong>
                {sampleCollectedDate} | {sampleCollectedTime}
                <UpdateIcon
                  sx={{
                    color: "#52b788",
                    marginLeft: 1,
                    "&:hover": { color: "#74c69d" },
                    cursor: "pointer",
                  }}
                  onClick={() => setOpenModal(true)}
                />
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
      {investigations?.map((inv, index) => (
        <MicroBioInvestigation
          key={inv._id}
          index={index}
          investigation={inv}
          compounds={inv.compound}
          invoice={selectedInvoice}
          sampleCollectedDate={sampleCollectedDate}
          sampleCollectedTime={sampleCollectedTime}
        />
      ))}
    </>
  );
};

export default MicroBiology;
